import { useState, useCallback } from 'react';
import { BigNum } from '@velocity-exchange/sdk';
import { TpSlInputSource } from 'src/components/TradeForm/TpSlOrderForm/types';

export interface TpSlOrderFormState {
	priceStringValue: string;
	percentStringValue: string;
	percent: number | null;
	price: BigNum | null;
	limitPriceStringValue: string;
	limitPrice: BigNum | null;
	priceInvalid: boolean;
	limitPriceInvalid: boolean;
	priceInvalidReason: 'entry' | 'immediate' | 'reference' | null;
	limitPriceInvalidReason: 'entry' | 'immediate' | 'reference' | null;
	/**
	 * The last input manually updated by the user.
	 * Used primarily to determine which input to auto-update when entry price changes.
	 */
	inputSource: TpSlInputSource;
}

interface Params {
	savedPrice: BigNum | null | undefined;
	savedLimitPrice?: BigNum | null | undefined;
	calcPercent: (price: BigNum) => number;
}

export default function useTpSlOrderFormState({
	savedPrice,
	savedLimitPrice,
	calcPercent,
}: Params) {
	const [
		{
			priceStringValue,
			percentStringValue,
			percent,
			price,
			limitPriceStringValue,
			limitPrice,
			inputSource,
			priceInvalid,
			limitPriceInvalid,
			priceInvalidReason,
			limitPriceInvalidReason,
		},
		setState,
	] = useState<TpSlOrderFormState>(() => {
		const priceForPercentCalc = savedLimitPrice ?? savedPrice;

		return {
			priceStringValue: savedPrice ? savedPrice.toTradePrecision() : '',
			percentStringValue: priceForPercentCalc
				? calcPercent(priceForPercentCalc).toFixed(2)
				: '',
			percent: null,
			price: savedPrice ?? null,
			limitPriceStringValue: savedLimitPrice
				? savedLimitPrice.toTradePrecision()
				: '',
			limitPrice: savedLimitPrice ?? null,
			inputSource: undefined,
			priceInvalid: false,
			limitPriceInvalid: false,
			priceInvalidReason: null,
			limitPriceInvalidReason: null,
		};
	});

	const clearState = useCallback(() => {
		setState({
			priceStringValue: '',
			percentStringValue: '',
			percent: null,
			price: null,
			limitPriceStringValue: '',
			limitPrice: null,
			inputSource: undefined,
			priceInvalid: false,
			limitPriceInvalid: false,
			priceInvalidReason: null,
			limitPriceInvalidReason: null,
		});
	}, []);

	return {
		priceStringValue,
		percentStringValue,
		percent,
		price,
		limitPriceStringValue,
		limitPrice,
		priceInvalid,
		limitPriceInvalid,
		priceInvalidReason,
		limitPriceInvalidReason,
		inputSource,
		clearState,
		setState,
	};
}
